/**
 * Purchase-conversion emitter — deduped, paid-only, server-verified.
 *
 * Extracted from `pages/PaymentPages.jsx` so the exactly-once /
 * paid-only contract can be exercised in isolation without a React
 * tree.
 *
 * Exactly-once guarantees
 * -----------------------
 *   * Outer sentinel (24h TTL in `localStorage.mps_purchase_fired_v1`)
 *     survives page reload, back navigation, tab restarts.
 *   * Inner sentinel (per-tab `sessionStorage.mps_ga4_purchase_fired_v1`)
 *     inside `analytics.js::trackPurchase` — belt & braces.
 *
 * Emission channels (all gated by `order.status === "paid"`):
 *   1. `dataLayer.push({event:'purchase',          ecommerce:{…}})`
 *   2. `dataLayer.push({event:'purchase_verified', ecommerce:{…}})`
 *   3. `gtag('event','purchase',{transaction_id, value, currency, items, tax, shipping})`
 *   4. `gtag('event','conversion',{send_to:'AW-.../<label>', value, currency,
 *      transaction_id})` — only when a Google Ads Purchase label is
 *      configured on `/api/config/public`.  Never invented.
 *
 * Never emitted from this module:
 *   * `page_view` on `/payment/success` — a page-load / URL-based
 *     conversion would fire on unpaid sessions too and violate the
 *     "no purchase from a page load or URL rule" invariant.
 */

import { trackPurchase } from "./analytics";

export const PURCHASE_FIRED_KEY  = "mps_purchase_fired_v1";
export const PURCHASE_FIRED_TTL  = 24 * 60 * 60 * 1000;  // 24h

// Google Ads defaults for this catalog. Both are compile-time constants
// so `/payment/success` fires the correct conversion even when
// `/api/config/public` has not been populated with the merchant's label.
// The admin can still override either value by setting a non-empty
// `google_ads_conversion_id` / `google_ads_purchase_conversion_label`
// on the public config endpoint (see `_get_public_config` in the
// backend); explicit-null via that endpoint continues to disable the
// tag by falling back to these defaults instead of firing nothing.
export const DEFAULT_ADS_CONVERSION_ID    = "AW-18375113165";
export const DEFAULT_ADS_PURCHASE_LABEL   = "AtVrCJvdr-gcEM3z97lE";

export function hasPurchaseFired(txnId) {
  try {
    const raw = localStorage.getItem(PURCHASE_FIRED_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw);
    const t = map[txnId];
    return typeof t === "number" && Date.now() - t < PURCHASE_FIRED_TTL;
  } catch { return false; }
}

export function markPurchaseFired(txnId) {
  try {
    const raw = localStorage.getItem(PURCHASE_FIRED_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[txnId] = Date.now();
    // GC entries older than TTL to keep the map small.
    const cutoff = Date.now() - PURCHASE_FIRED_TTL;
    for (const k of Object.keys(map)) if (map[k] < cutoff) delete map[k];
    localStorage.setItem(PURCHASE_FIRED_KEY, JSON.stringify(map));
  } catch { /* no-op */ }
}

/**
 * Fire the standards-compliant GA4 + Google Ads conversion events.
 * `order` must be the server-verified `/api/orders/session/{sid}` body.
 * `config` is the `/api/config/public` payload (safe to be empty).
 */
export function firePurchaseConversion(order, config) {
  if (!order || !order.session_id || order.status !== "paid") return { fired: false, reason: "not_paid" };
  const txnId = order.session_id;
  if (hasPurchaseFired(txnId))                                   return { fired: false, reason: "already_fired" };

  const valueDollars = (order.total_cents || 0) / 100;
  const currency     = (order.currency || "usd").toUpperCase();

  // 1) + 2) — dataLayer `purchase` + `purchase_verified`.
  trackPurchase({
    session_id:      txnId,
    status:          "paid",
    total_cents:     order.total_cents,
    tax_cents:       order.tax_cents,
    shipping_cents:  order.shipping_cents,
    currency,
    items:           order.items || [],
  });

  // 3) Direct GA4 hit via gtag.
  const items = (order.items || []).map((it) => ({
    item_id:   it.public_sku || it.product_id,
    item_name: it.public_display_name || it.product_id,
    price:     (it.unit_amount_cents || 0) / 100,
    quantity:  it.quantity,
  }));
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", "purchase", {
        transaction_id: txnId,
        value:          valueDollars,
        currency,
        tax:            order.tax_cents != null ? order.tax_cents / 100 : undefined,
        shipping:       order.shipping_cents != null ? order.shipping_cents / 100 : undefined,
        items,
      });
    } catch (_) { /* no-op */ }
  }

  // 4) Google Ads event-based conversion. Uses the compile-time defaults
  //    unless the admin has explicitly set a non-empty override on
  //    `/api/config/public`. This guarantees the correct destination
  //    (`AW-18375113165/AtVrCJvdr-gcEM3z97lE`) fires for every paid
  //    session without waiting on an admin config edit.
  const adsId = (config && config.google_ads_conversion_id) || DEFAULT_ADS_CONVERSION_ID;
  const label = (config && config.google_ads_purchase_conversion_label) || DEFAULT_ADS_PURCHASE_LABEL;
  if (adsId && label && typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", "conversion", {
        send_to:        `${adsId}/${label}`,
        value:          valueDollars,
        currency,
        transaction_id: txnId,
      });
    } catch (_) { /* no-op */ }
  }

  markPurchaseFired(txnId);
  const outcome = { fired: true, reason: "ok", transaction_id: txnId, value: valueDollars, currency };

  // Server-side diagnostic beacon (privacy-safe, fire-and-forget). Lets
  // the merchant confirm from server logs that the browser reached
  // /payment/success AND attempted the tag dispatch — the critical
  // signal for debugging "Google Ads shows 0 conversions" tickets
  // without needing the customer's browser open. Never blocks the
  // return: the promise is intentionally not awaited.
  sendConversionBeacon({
    session_id:     txnId,
    transaction_id: txnId,
    value_cents:    order.total_cents,
    currency,
    ga4_purchase:   { fired: true, reason: "ok" },
    ads_conversion: { fired: !!(adsId && label), reason: (adsId && label) ? "ok" : "no_config" },
  });

  return outcome;
}

/**
 * POST the sanitized dispatch outcome to /api/track/purchase-conversion.
 * Never blocks. Never throws. The endpoint is idempotent from the
 * server's perspective — the outer `hasPurchaseFired` sentinel already
 * guarantees exactly-once from the client, and each beacon row is
 * uniquely keyed by a server-side uuid so duplicate rows are impossible
 * even if the client accidentally retries.
 */
function sendConversionBeacon(payload) {
  try {
    if (typeof window === "undefined") return;
    const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
    const url = `${base}/api/track/purchase-conversion`;
    // Prefer sendBeacon so the request survives a page unload (common
    // after /payment/success — shopper may close the tab immediately
    // after seeing "Thank you").
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        if (navigator.sendBeacon(url, blob)) return;
      } catch (_) { /* fall through to fetch */ }
    }
    if (typeof fetch === "function") {
      // Fire-and-forget; do NOT await.
      fetch(url, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        body:        JSON.stringify(payload),
        keepalive:   true,
        credentials: "omit",
      }).catch(() => { /* no-op */ });
    }
  } catch (_) { /* never let the beacon break the success page */ }
}
